import type { AuthInfo } from '~/types/interfaces'

interface AuthResponse {
  status: string
  auth?: AuthInfo
}

const REFRESH_SAFETY_WINDOW_SEC = 300

let refreshPromise: Promise<void> | null = null

/**
 * Cognito トークンの proactive / reactive refresh を司るシングルトン。
 *
 * - `refreshIfNeeded()`: API 呼び出し前にチェックし、`exp` まで 5 分未満なら
 *   バックエンド `/api/v1/auth?challenge=refresh` を叩いて新 access/id token に
 *   差し替える。余裕があれば何もしない。
 * - `forceRefresh()`: 401 を受けた直後などに明示的にリフレッシュを試みる。
 *
 * 並行リクエスト対策としてモジュールレベルのシングルトン Promise を保持し、
 * 同時に複数の fetch が refresh を起動しても 1 度しか走らないようにする。
 *
 * サーキットブレーカ (#600): `authStateStore.isSessionExpired === true` の間は
 * 全 refresh 呼び出しを即座に reject する。これがないと:
 *   - notificationPoller / 他の API 呼び出しから次々と refresh が走る
 *   - 各 refresh は dead refresh_token で 401 → Cognito → Discord 通知が連打される
 * `markSessionExpired()` は authenticatedFetch が refresh 失敗を検出したときに立つ。
 * 再ログイン経路（signin / google callback）はどちらも `authStateStore.reset()` を
 * 呼ぶため、フラグ管理は authStateStore 1 箇所に集約される。
 */
export function useTokenRefresh() {
  const config = useRuntimeConfig()
  const apiBase = config.public.apiBase as string
  const authInfoStore = useAuthInfoStore()
  const authStateStore = useAuthStateStore()

  const doRefresh = async (): Promise<void> => {
    const refreshToken = authInfoStore.refreshToken
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const resp = await $fetch<AuthResponse>(`${apiBase}/api/v1/auth`, {
      method: 'POST',
      query: { challenge: 'refresh' },
      headers: { 'Content-Type': 'application/json' },
      body: {
        refreshToken,
        // OAuth client への REFRESH_TOKEN_AUTH で使う SECRET_HASH 計算用 (#600)。
        // フェデレーションユーザーでは sub != cognito_username なので必ず cognito_username を送る。
        cognitoUsername: authInfoStore.cognitoUsername || undefined,
        // 旧バックエンド互換のフォールバック（次の deploy 後は削除予定）。
        sub: authInfoStore.sub || undefined,
      },
    })

    if (resp.status !== 'success' || !resp.auth) {
      throw new Error(`Refresh returned status=${resp.status}`)
    }

    authInfoStore.setAuthInfo(resp.auth)
    authStateStore.reset()
  }

  const startRefresh = (): Promise<void> => {
    // サーキットブレーカ: セッション切れ後はサインインまで refresh しない (#600)。
    if (authStateStore.isSessionExpired) {
      return Promise.reject(new Error('Refresh circuit broken — session expired'))
    }
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => {
        refreshPromise = null
      })
    }
    return refreshPromise
  }

  const refreshIfNeeded = async (): Promise<void> => {
    if (authStateStore.isSessionExpired) return
    const nowSec = Date.now() / 1000
    if (authInfoStore.exp > nowSec + REFRESH_SAFETY_WINDOW_SEC) return
    if (!authInfoStore.refreshToken) return
    await startRefresh()
  }

  const forceRefresh = async (): Promise<void> => {
    await startRefresh()
  }

  return { refreshIfNeeded, forceRefresh }
}
